import { createContext, useContext, useState, useCallback, useEffect, ReactNode, useMemo } from "react";
import { useRole } from "./RoleContext";
import { useUserSession } from "@/hooks/use-user-session";
import { useLocation } from "react-router-dom";

interface LocationOption {
  location_id: string;
  name: string;
  state: string | null;
}

interface ActiveLocationContextType {
  /** The currently active location ID for center-scoped views */
  activeLocationId: string | null;
  /** All locations this user is assigned to */
  userLocations: LocationOption[];
  /** Whether the user has multiple locations (multi-center tutor) */
  isMultiCenter: boolean;
  /** Whether the center picker should be shown */
  showCenterPicker: boolean;
  /** Set the active location */
  setActiveLocation: (locationId: string) => void;
  /** Open center picker dialog */
  openCenterPicker: () => void;
  /** Close center picker dialog */
  closeCenterPicker: () => void;
  /** Loading state */
  isLoading: boolean;
}

const ActiveLocationContext = createContext<ActiveLocationContextType | undefined>(undefined);

const ACTIVE_LOCATION_KEY = "lurneax_active_location";

export function ActiveLocationProvider({ children }: { children: ReactNode }) {
  const { userId, userRole } = useRole();
  const { data: session, isLoading } = useUserSession();
  const location = useLocation();
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);
  const [showCenterPicker, setShowCenterPicker] = useState(false);
  const [hasShownOnLogin, setHasShownOnLogin] = useState(false);

  // Don't show center picker while tutor is on profile page (profile not yet complete)
  const isTutorOnProfile = userRole === "tutor" && location.pathname === "/tutor-profile";

  // Derive locations from the shared user-session query — no extra round-trip.
  const userLocations = useMemo<LocationOption[]>(
    () =>
      (session?.locations ?? []).map((row) => ({
        location_id: row.location_id,
        name: (row.locations as any)?.name || "Unknown",
        state: (row.locations as any)?.state || null,
      })),
    [session?.locations]
  );

  const isMultiCenter = userLocations.length > 1 && userRole === "tutor";

  // On locations load, decide whether to show picker
  useEffect(() => {
    if (isLoading || !userId || userLocations.length === 0) return;

    // Don't show center picker while tutor profile is being completed
    if (isTutorOnProfile) return;

    if (isMultiCenter) {
      const stored = localStorage.getItem(ACTIVE_LOCATION_KEY);
      const storedValid = stored && userLocations.some((l) => l.location_id === stored);

      if (storedValid) {
        setActiveLocationId(stored);
        // Don't re-prompt — user already chose a center
      } else {
        // No valid stored location — must pick
        setShowCenterPicker(true);
        setHasShownOnLogin(true);
      }
    } else if (userLocations.length === 1) {
      setActiveLocationId(userLocations[0].location_id);
      localStorage.setItem(ACTIVE_LOCATION_KEY, userLocations[0].location_id);
    }
  }, [isLoading, userId, userLocations, isMultiCenter, hasShownOnLogin, isTutorOnProfile]);

  // Reset on user change (logout/login)
  useEffect(() => {
    if (!userId) {
      setActiveLocationId(null);
      setShowCenterPicker(false);
      setHasShownOnLogin(false);
      localStorage.removeItem(ACTIVE_LOCATION_KEY);
    }
  }, [userId]);

  const setActiveLocation = useCallback((locationId: string) => {
    setActiveLocationId(locationId);
    localStorage.setItem(ACTIVE_LOCATION_KEY, locationId);
    setShowCenterPicker(false);
  }, []);

  const openCenterPicker = useCallback(() => setShowCenterPicker(true), []);
  const closeCenterPicker = useCallback(() => {
    // Only allow close if a location is already selected
    if (activeLocationId) setShowCenterPicker(false);
  }, [activeLocationId]);

  return (
    <ActiveLocationContext.Provider
      value={{
        activeLocationId,
        userLocations,
        isMultiCenter,
        showCenterPicker,
        setActiveLocation,
        openCenterPicker,
        closeCenterPicker,
        isLoading,
      }}
    >
      {children}
    </ActiveLocationContext.Provider>
  );
}

export function useActiveLocation() {
  const context = useContext(ActiveLocationContext);
  if (!context) {
    throw new Error("useActiveLocation must be used within ActiveLocationProvider");
  }
  return context;
}
